import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  ButtonGroup,
  CloseButton,
  Collapse,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Spacer,
  Spinner,
  Text,
  useBreakpointValue,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useOutsideClick,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  EditIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import API, { getMediaURL } from "../../context/API";
import { BiCollapseVertical } from "react-icons/bi";
import { Reorder, useDragControls } from "framer-motion";
import { MdDragHandle } from "react-icons/md";
import CustomizationTooltip from "./CustomizationTooltip/CustomizationTooltip";

// Memoized MenuItem component
const MenuItem = React.memo(({
  item,
  categoryId,
  updateItemOrder,
  onEdit,
  onDelete,
  onCustomizationClick,
}) => {
  const [price, setPrice] = useState(item.price);
  const [soldOut, setSoldOut] = useState(item.sold_out);
  const { colorMode } = useColorMode();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleEditClick = useCallback(() => {
    onEdit(item);
  }, [item, onEdit]);

  const handleDeleteClick = useCallback(() => {
    onDelete(item.id);
  }, [item.id, onDelete]);

  const handlePriceChange = useCallback((e) => {
    const newPrice = parseFloat(e.target.value);
    setPrice(newPrice);
    updateItemOrder(categoryId, item.id, newPrice);
  }, [categoryId, item.id, updateItemOrder]);

  return (
    <Box
      key={item.id}
      mb="8px"
      p={{ base: "3", md: "4" }}
      pl={{ base: "3", md: "16" }}
      borderWidth="1px"
      borderRadius="lg"
      bg={colorMode === "light" ? "white" : "transparent"}
      position="relative"
    >
      <Flex
        justify="space-between"
        align="center"
        direction={{ base: "column", md: "row" }}
        w="100%"
      >
        <Flex
          align="center"
          mb={{ base: "3", md: "0" }}
          w={{ base: "100%", md: "auto" }}
        >
          <Image
            src={item.picture}
            boxSize={{ base: "36px", md: "50px" }}
            objectFit="cover"
            borderRadius="md"
            mr="3"
            alt={item.item}
            fallbackSrc="https://via.placeholder.com/50"
            loading="lazy"
          />
          <VStack align="flex-start" spacing={{ base: "1", md: "1" }} w="100%">
            <Text
              fontWeight="bold"
              fontSize={{ base: "sm", md: "md" }}
              noOfLines={1}
            >
              {item.item}
            </Text>
            <HStack display="flex" flexWrap="wrap" w="100%" maxW={{ base: "100%", md: "400px" }}>
              {item.customizations?.map((custom) => (
                <CustomizationTooltip
                  key={custom.id || custom.customization.id}
                  modifierGroupText="Modifier Group"
                  customizationName={custom?.customization?.customization}
                  options={custom?.customization?.option}
                  sold_out={custom?.customization?.sold_out}
                  placement="top"
                  customization={custom}
                  onCustomizationClick={onCustomizationClick}
                >
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    noOfLines={1}
                    maxW={{ base: "80px", md: "150px" }}
                    _hover={{ fontWeight: "bold" }}
                    cursor="pointer"
                    onClick={() => onCustomizationClick(custom?.customization?.id)}
                  >
                    {custom?.customization?.customization},
                  </Text>
                </CustomizationTooltip>
              ))}
            </HStack>
          </VStack>
        </Flex>

        <Flex
          w={{ base: "100%", md: "auto" }}
          justifyContent="space-between"
          alignItems="center"
          mt={{ base: "2", md: "0" }}
          direction={{ base: "row", md: "row" }}
        >
          <Flex>
            {soldOut && (
              <Text
                minWidth={"60px"}
                border="1px solid"
                borderColor="gray.300"
                borderRadius="md"
                backgroundColor="gray.100"
                fontSize={{ base: "sm", md: "sm" }}
                mr="2"
                px={2}
                py={1}
                textColor={"red.300"}
                display="inline-block"
              >
                Sold Out
              </Text>
            )}
          </Flex>
          <input
            type="number"
            value={item.price != null ? item.price.toFixed(2) : ""}
            onChange={handlePriceChange}
            style={{
              padding: "5px",
              fontSize: isMobile ? "12px" : "14px",
              maxWidth: isMobile ? "60px" : "70px",
              marginRight: "10px",
              borderRadius: "4px",
            }}
            disabled={true}
          />
          <Flex flexWrap={{ base: "wrap", md: "nowrap" }}>
            <IconButton
              icon={<EditIcon />}
              size="sm"
              onClick={handleEditClick}
              aria-label="Edit item"
              mr={{ base: "1", md: "2" }}
            />
            <Button
              fontSize={{ base: "xs", md: "sm" }}
              border="1px solid"
              borderColor="red.300"
              textColor={soldOut ? "green.300" : "red.500"}
              py={{ base: "1", md: "2" }}
              px={{ base: "2", md: "3" }}
              height={{ base: "32px", md: "auto" }}
              onClick={() => {
                onEdit(item, true);
                setSoldOut((prev) => !prev);
              }}
              mb={{ base: "1", md: "0" }}
            >
              {soldOut ? (isMobile ? "In Stock" : "Set In Stock") : (isMobile ? "Sold Out" : "Set Sold Out")}
            </Button>
            <IconButton
              icon={<DeleteIcon />}
              size="sm"
              onClick={handleDeleteClick}
              aria-label="Delete item"
              colorScheme="red"
              ml={{ base: "1", md: "2" }}
            />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
});

// Memoized MenuCategory component
const MemoizedMenuItem = React.memo(MenuItem);

const MenuCategory = React.memo(({
  category,
  categoryItems,
  isOpen,
  toggleCategory,
  loadingItems,
  updateItemOrder,
  onEdit,
  onDelete,
  query,
  onCustomizationClick,
}) => {
  const { colorMode } = useColorMode();
  const [items, setItems] = useState(categoryItems || []);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimeoutRef = useRef(null);

  // Responsive values
  const paddingValues = { base: "3", md: "4" };
  const dragIconSize = { base: 5, md: 6 };

  useEffect(() => {
    setItems(categoryItems || []);
  }, [categoryItems]);

  const handleToggle = useCallback(() => {
    setIsTransitioning(true);
    toggleCategory(category.id);
    
    // Set timeout based on estimated content size
    const estimatedItems = items.length;
    const transitionDuration = Math.min(1000, Math.max(200, estimatedItems * 20));
    
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, transitionDuration);
  }, [toggleCategory, category.id, items.length]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  const renderedItems = useMemo(() => (
    items.map((item) => (
      <Reorder.Item 
        key={item.id} 
        value={item}
        style={{ willChange: 'transform' }}
      >
        <MemoizedMenuItem
          item={item}
          categoryId={category.id}
          updateItemOrder={updateItemOrder}
          onEdit={onEdit}
          onDelete={onDelete}
          onCustomizationClick={onCustomizationClick}
        />
      </Reorder.Item>
    ))
  ), [items, category.id, updateItemOrder, onEdit, onDelete, onCustomizationClick]);

  return (
    <Reorder.Item
      value={category.id}
      style={{ listStyleType: "none", width: "100%" }}
      key={category.id}
    >
      <VStack spacing={2} align="stretch" width="100%" mb={4}>
        {/* Category Header with Loading State */}
        <Flex
          justify="space-between"
          align="center"
          p={paddingValues}
          bg={colorMode === "light" ? "white" : "gray.800"}
          boxShadow="sm"
          cursor={isTransitioning ? 'wait' : 'pointer'}
          onClick={isTransitioning ? undefined : handleToggle}
          borderBottomWidth="1px"
          width="100%"
          borderRadius="md"
          position="relative"
          opacity={isTransitioning ? 0.8 : 1}
        >
          {isTransitioning && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg={colorMode === 'light' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)'}
              zIndex={1}
            >
              <Spinner size="sm" />
            </Box>
          )}

          <Box
            cursor="grab"
            mr={{ base: "2", md: "4" }}
            borderWidth="1px"
            borderRadius="md"
            px={{ base: "2", md: "4" }}
            py={{ base: "1", md: "2" }}
          >
            <Icon
              as={MdDragHandle}
              w={dragIconSize}
              h={dragIconSize}
            />
          </Box>

          <Flex align="center" flex="1">
            <VStack align="flex-start" spacing="0" ml={{ base: "2", md: "4" }}>
              <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
                {category.category}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                {items.length} items
              </Text>
            </VStack>
          </Flex>

          <Box>
            {isTransitioning ? (
              <Spinner size="sm" />
            ) : (
              isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />
            )}
          </Box>
        </Flex>

        {/* Collapsible Content */}
        <Collapse 
          in={isOpen} 
          animateOpacity
          style={{
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDuration: '200ms'
          }}
        >
          <Box px={{ base: "0", md: "2" }}>
            {loadingItems ? (
              <Box py={4}>
                <Spinner size="md" />
              </Box>
            ) : (
              <Reorder.Group
                axis="y"
                values={items}
                style={{ willChange: 'transform' }}
              >
                {renderedItems}
              </Reorder.Group>
            )}
          </Box>
        </Collapse>
      </VStack>
    </Reorder.Item>
  );
});


const MenuOverview = ({
  refreshTrigger,
  onEditItem,
  handleAddItem,
  handleSaveItem,
  categoriesToRefetch,
  onCategoriesRefetched,
  onCustomizationClick,
}) => {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [areAllCollapsed, setAreAllCollapsed] = useState(true);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [categoryItemsMap, setCategoryItemsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingItems, setLoadingItems] = useState({});
  const [openedCategories, setOpenedCategories] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandAll, setExpandAll] = useState(false);
  const [allItems, setAllItems] = useState([]);

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelRef = useRef();
  const toast = useToast();

  const suggestionBoxRef = useRef();
  const bgColor = useColorModeValue("white", "gray.800");
  const bgHoverColor = useColorModeValue("gray.200", "gray.700");
  const focusBorderColor = useColorModeValue("gray.700", "gray.200");
  const shadowColor = useColorModeValue("md", "dark-lg");
  const buttonSize = useBreakpointValue({ base: "sm", md: "md" });
  const spacing = useBreakpointValue({ base: "2", md: "4" });

  useOutsideClick({
    ref: suggestionBoxRef,
    handler: () => setIsSearching(false),
  });

  const fetchCategories = useCallback(async () => {
    try {
      const response = await API.get("/categories");
      const categoriesData = response.data.data.map((category) => {
        const itemsArray = category.items || [];
        const itemsData = Array.isArray(itemsArray)
          ? itemsArray.map((item) => ({
            ...item,
            picture: getMediaURL(item.picture),
          }))
          : [];

        itemsData.sort((a, b) => a.sequence_number - b.sequence_number);

        categoryItemsMap[category.id] = itemsData;

        return {
          ...category,
          picture: getMediaURL(category.picture),
          items: itemsData.map((item) => item.id.toString()),
        };
      });

      categoriesData.sort((a, b) => a.sequence_number - b.sequence_number);

      setCategoryItemsMap((prevMap) => ({
        ...prevMap,
        ...categoryItemsMap,
      }));
      setCategories(categoriesData);
      setCategoryOrder(categoriesData.map((cat) => cat.id));
      setFilteredCategories(categoriesData);
      setOpenedCategories([]);
      setAreAllCollapsed(true);
      setExpandAll(false);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories, refreshTrigger]);

  useEffect(() => {
    const combinedItems = categories.reduce((acc, category) => {
      const items = categoryItemsMap[category.id] || [];
      return acc.concat(
        items.map((item) => ({
          ...item,
          categoryId: category.id,
          categoryName: category.category,
        }))
      );
    }, []);
    setAllItems(combinedItems);
  }, [categoryItemsMap, categories]);

  useEffect(() => {
    if (categoriesToRefetch && categoriesToRefetch.length > 0) {
      categoriesToRefetch.forEach((categoryId) => {
        fetchCategoryItems(categoryId);
      });
      onCategoriesRefetched();
    }
  }, [categoriesToRefetch, onCategoriesRefetched]);

  const fetchCategoryItems = useCallback(async (categoryId) => {
    if (loadingItems[categoryId] ||
      (categoryItemsMap[categoryId] && categoryItemsMap[categoryId].length > 0)) {
      return;
    }

    setLoadingItems((prev) => ({ ...prev, [categoryId]: true }));

    try {
      const response = await API.get(`/categories/${categoryId}/items`);
      const itemsArray = response.data.data || response.data.items || response.data || [];

      const itemsData = Array.isArray(itemsArray)
        ? itemsArray.map((item) => ({
          ...item,
          picture: getMediaURL(item.picture),
        }))
        : [];

      itemsData.sort((a, b) => a.sequence_number - b.sequence_number);

      setCategoryItemsMap((prevMap) => ({
        ...prevMap,
        [categoryId]: itemsData,
      }));
    } catch (error) {
      console.error("Error fetching items:", error);
      toast({
        title: "Error loading items",
        description: "Could not load items for this category.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingItems((prev) => ({ ...prev, [categoryId]: false }));
    }
  }, [loadingItems, categoryItemsMap, toast]);

  const toggleCategory = useCallback((categoryId) => {
    setOpenedCategories((prevOpened) => {
      const isOpened = prevOpened.includes(categoryId);
      if (isOpened) {
        return prevOpened.filter((id) => id !== categoryId);
      }
      if (categoryItemsMap[categoryId] && categoryItemsMap[categoryId].length > 0) {
        return [...prevOpened, categoryId];
      }
      fetchCategoryItems(categoryId);
      return [...prevOpened, categoryId];
    });
  }, [categoryItemsMap, fetchCategoryItems]);

  const preloadNearbyCategories = useCallback((currentCategoryId) => {
    const currentIndex = categories.findIndex(cat => cat.id === currentCategoryId);
    if (currentIndex !== -1 && currentIndex < categories.length - 1) {
      const nextCategoryId = categories[currentIndex + 1].id;
      if (!categoryItemsMap[nextCategoryId] || categoryItemsMap[nextCategoryId].length === 0) {
        fetchCategoryItems(nextCategoryId);
      }
    }
  }, [categories, categoryItemsMap, fetchCategoryItems]);

  useEffect(() => {
    if (openedCategories.length > 0) {
      const lastOpenedCategory = openedCategories[openedCategories.length - 1];
      preloadNearbyCategories(lastOpenedCategory);
    }
  }, [openedCategories, preloadNearbyCategories]);

  const handleReOrder = useCallback(async (reorderData) => {
    await API.put(`/categories/sequence-numbers`, {
      categories: reorderData,
    });
  }, []);

  const handleReorder = useCallback(async (newOrder) => {
    setCategoryOrder(newOrder);

    const reorderedCategories = newOrder.map((id, index) => {
      const category = categories.find((cat) => cat.id === id);
      return {
        ...category,
        sequence_number: index + 1,
      };
    });
    const reorderedData = reorderedCategories.map((category) => ({
      id: parseInt(category.id),
      sequence_number: category.sequence_number,
    }));

    setCategories(reorderedCategories);
    try {
      await handleReOrder(reorderedData);
      toast({
        title: "Categories reordered.",
        description: "The categories have been reordered successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error updating sequence numbers:", error);
      toast({
        title: "Error updating order.",
        description: "Unable to update the category order.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [categories, handleReOrder, toast]);

  const toggleAllCategories = useCallback(() => {
    if (expandAll) {
      setOpenedCategories([]);
      setAreAllCollapsed(true);
    } else {
      const allCategoryIds = categories.map((category) => category.id);
      setOpenedCategories(allCategoryIds);
      setAreAllCollapsed(false);
    }
    setExpandAll(!expandAll);
  }, [categories, expandAll]);

  const matchingCategories = useCallback((searchTerm) => {
    if (!searchTerm) return [];
    return categories.filter((category) =>
      category.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categories]);

  const matchingItems = useCallback((searchTerm) => {
    if (!searchTerm) return [];
    const term = searchTerm.toLowerCase();
    return allItems.filter((item) =>
      item.item.toLowerCase().includes(term)
    );
  }, [allItems]);

  // Add state for current matches
  const [currentCategoryMatches, setCurrentCategoryMatches] = useState([]);
  const [currentItemMatches, setCurrentItemMatches] = useState([]);

  const searchTimeout = useRef(null);
  const handleSearchChange = useCallback((e) => {
    const searchTerm = e.target.value.toLowerCase();
    setQuery(searchTerm);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (searchTerm === "") {
      setFilteredCategories(categories);
      setCategoryOrder(categories.map((cat) => cat.id));
      setIsSearching(false);
      setCurrentCategoryMatches([]);
      setCurrentItemMatches([]);
      return;
    }

    searchTimeout.current = setTimeout(() => {
      const catMatches = matchingCategories(searchTerm);
      const itemMatches = matchingItems(searchTerm);

      // Store the current matches in state
      setCurrentCategoryMatches(catMatches);
      setCurrentItemMatches(itemMatches);

      setIsSearching(true);

      if (catMatches.length > 0) {
        setFilteredCategories(catMatches);
        setCategoryOrder(catMatches.map((cat) => cat.id));
      } else if (itemMatches.length > 0) {
        const categoryIds = [...new Set(itemMatches.map(item => item.categoryId))];
        const matchedCategories = categories.filter(cat => categoryIds.includes(cat.id));
        setFilteredCategories(matchedCategories);
        setCategoryOrder(matchedCategories.map(cat => cat.id));
      } else {
        setFilteredCategories([]);
        setCategoryOrder([]);
      }
    }, 300);
  }, [categories, allItems, matchingCategories, matchingItems]);

  const clearSearch = useCallback(() => {
    setQuery("");
    setFilteredCategories(categories);
    setIsSearching(false);
    setCategoryOrder(categories.map((cat) => cat.id));
  }, [categories]); // Make sure to include all dependencies

  const handleCategorySelect = useCallback((categoryId) => {
    const selected = categories.find((category) => category.id === categoryId);
    if (selected) {
      setFilteredCategories([selected]);
      setCategoryOrder([selected.id]);
    }
    setIsSearching(false);
  }, [categories]);

  const handleItemSelect = useCallback((itemId) => {
    const selectedItem = allItems.find((item) => item.id === itemId);
    if (selectedItem) {
      const category = categories.find(
        (cat) => cat.id === selectedItem.categoryId
      );
      setFilteredCategories([category]);
      setCategoryOrder([category.id]);
    }
    setIsSearching(false);
  }, [allItems, categories]);

  const confirmDelete = useCallback((id) => {
    setSelectedItem(id);
    onDeleteOpen();
  }, [onDeleteOpen]);

  const handleDelete = useCallback(async () => {
    try {
      await API.delete(`/items/${selectedItem}`);
      setCategoryItemsMap((prevMap) => {
        const updatedMap = { ...prevMap };
        for (const categoryId in updatedMap) {
          updatedMap[categoryId] = updatedMap[categoryId].filter(
            (item) => item.id !== selectedItem
          );
        }
        return updatedMap;
      });
      onDeleteClose();
      toast({
        title: "Item deleted.",
        description: "The item was successfully deleted.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting item:", error);
      toast({
        title: "An error occurred.",
        description: "Unable to delete the item.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [selectedItem, onDeleteClose, toast]);

  const updateItemOrder = useCallback((categoryId, newOrder) => {
    setCategoryItemsMap((prevMap) => ({
      ...prevMap,
      [categoryId]: newOrder,
    }));
  }, []);


  const renderSuggestionBox = () => {
    const hasSuggestions = currentCategoryMatches.length > 0 || currentItemMatches.length > 0;
  
    const listItemStyles = {
      cursor: "pointer",
      p: "2",
      borderRadius: "md",
      _hover: {
        bg: bgHoverColor,
        transition: "background-color 0.3s ease",
      }
    };
  
    return (
      <Box 
        ref={suggestionBoxRef}
        bg={bgColor}
        borderWidth="1px"
        borderRadius="md"
        shadow={shadowColor}
        zIndex="10"
        position="absolute"
        top={{ base: "48px", md: "60px" }}
        width="100%"
        overflowY="auto"
        // maxHeight={{ base: "700px", md: "550px" }}
        maxHeight="70vh"
        mt="2"
        px={{ base: "2", md: "4" }}
        py={{ base: "1", md: "2" }}
        // backgroundColor={'red'}
      >
        <List spacing={2}>
          {hasSuggestions ? (
            <>
              {/* Categories Section */}
              {currentCategoryMatches.map((category) => (
                <ListItem
                  key={`category-${category.id}`}
                  {...listItemStyles}
                  onClick={() => handleCategorySelect(category.id)}
                >
                  <Flex justify="space-between" align="center">
                    <Text>{category.category}</Text>
                    <Text fontSize="sm" color="gray.500">
                      Category
                    </Text>
                  </Flex>
                </ListItem>
              ))}
  
              {/* Items Section */}
              {currentItemMatches.map((item) => (
                <ListItem
                  key={`item-${item.id}`}
                  {...listItemStyles}
                  onClick={() => handleItemSelect(item.id)}
                >
                  <Flex justify="space-between" align="center">
                    <Text>{item.item}</Text>
                    <Text fontSize="sm" color="gray.500">
                      Item in {item.categoryName}
                    </Text>
                  </Flex>
                </ListItem>
              ))}
            </>
          ) : (
            <ListItem>
              <Text textAlign="center" color="gray.500">
                No matching categories or items found
              </Text>
            </ListItem>
          )}
        </List>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box my={"4"}>
      <>
        <Flex
          justify={"space-between"}
          alignItems={"center"}
          width={"100%"}
          flexWrap={"wrap"}
        >
          <Box>
            <Heading size={"xl"} letterSpacing={-2}>
              Menu
            </Heading>
            <Text fontSize={"sm"}>
              Menu overview, see different categories and items against these
              categories
            </Text>
          </Box>
        </Flex>

        <Flex width="100%" p={spacing} alignItems="center" >
          <Flex width="100%" alignItems="center" position="relative">
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                type="search"
                placeholder="Search categories..."
                _placeholder={{ color: "gray.500" }}
                value={query}
                onChange={handleSearchChange}
                onFocus={() => setIsSearching(true)}
                variant={"filled"}
                borderRadius={"full"}
                _focus={{
                  borderColor: focusBorderColor,
                }}
                sx={{
                  "::-webkit-search-cancel-button": {
                    appearance: "none",
                  },
                }}
              />
              <InputRightElement>
                {query && (
                  <CloseButton onClick={clearSearch} borderRadius={"full"} />
                )}
              </InputRightElement>
            </InputGroup>

            {isSearching && query && renderSuggestionBox()}
          </Flex>
          <Spacer />
          <Flex alignItems="flex-end">
            <ButtonGroup>
              <IconButton
                size={buttonSize}
                icon={<BiCollapseVertical />}
                bg="black"
                color={"white"}
                _hover={{
                  color: "black",
                  bg: "white",
                  borderWidth: "1px",
                  borderColor: "gray.300",
                }}
                variant="outline"
                aria-label={expandAll ? "Collapse All" : "Expand All"}
                ml={spacing}
                onClick={toggleAllCategories}
                mr={0}
                display={{ base: "flex", md: "none" }}
              />

              <Button
                leftIcon={<BiCollapseVertical />}
                bg="black"
                color={"white"}
                _hover={{
                  color: "black",
                  bg: "white",
                  borderWidth: "1px",
                  borderColor: "gray.300",
                }}
                variant="outline"
                onClick={toggleAllCategories}
                aria-label={expandAll ? "Collapse All" : "Expand All"}
                ml={spacing}
                display={{ base: "none", md: "flex" }}
              >
                {expandAll ? "Collapse All" : "Expand All"}
              </Button>
              <IconButton
                size={buttonSize}
                icon={<AddIcon />}
                bg="black"
                color={"white"}
                _hover={{
                  color: "black",
                  bg: "white",
                  borderWidth: "1px",
                  borderColor: "gray.300",
                }}
                variant="outline"
                aria-label="Add item"
                ml={spacing}
                onClick={handleAddItem}
                mr={0}
              />
            </ButtonGroup>
          </Flex>
        </Flex>
      </>
      <Box p={4} bg={bgColor} borderRadius="md">
        <Reorder.Group
          as="div"
          axis="y"
          values={categoryOrder}
          onReorder={handleReorder}
          style={{ listStyleType: "none" }}
        >
          {categoryOrder.map((id) => {
            const category = filteredCategories.find((cat) => cat.id === id);
            if (!category) return null;

            return (
              <Reorder.Item
                as="div"
                key={id}
                value={id}
                style={{ listStyleType: "none" }}
                dragListener={false}
              >
                <MenuCategory
                  category={category}
                  categoryItems={categoryItemsMap[category.id] || []}
                  isOpen={openedCategories.includes(category.id)}
                  loadingItems={loadingItems[category.id]}
                  toggleCategory={toggleCategory}
                  updateItemOrder={updateItemOrder}
                  onEdit={onEditItem}
                  onDelete={confirmDelete}
                  query={query}
                  onCustomizationClick={onCustomizationClick}
                />
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
      </Box>

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Item
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default React.memo(MenuOverview);